import { baseQuery } from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { TransactionRequest, TransactionDownloadRequest } from "shared/models/transaction.model";
import moment from "moment";
import { DisbursementDownloadRequest } from "shared/models/disbursements.model";
import { ChargeAutomationDownloadRequest } from "shared/models/chargeAuto.models";
import { Chargeback, ChargebackDownload, ChargebackRequest } from "shared/models/chargeback.model";
import { DataResponse } from "shared/models";
import { FileName } from "constants/common.constants";

export const reportService = createApi({
  reducerPath: "reportService",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getTransactions: builder.query<any, TransactionRequest>({
      query: ({ nameMerchant, skip_log, ...others }) => ({
        url: `/report/transactions`,
        method: "GET",
        params: others,
        headers: !skip_log
          ? {
              PageName: "Transaction",
            }
          : {},
      }),
    }),
    getTransactionsPost: builder.mutation<any, any>({
      query: ({ nameMerchant, skip_log, ...others }) => ({
        url: `/report/transactions`,
        method: "POST",
        body: others,
        headers: !skip_log
          ? {
              PageName: "Transaction",
            }
          : {},
      }),
    }),
    getDisbursementReport: builder.query<any, any>({
      query: (params) => ({
        url: `/report/disbursements`,
        method: "GET",
        params,
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout"
            : "Payout Management",
        },
      }),
    }),
    getDisbursementReportPost: builder.mutation<any, any>({
      query: (params) => ({
        url: `/report/disbursements`,
        method: "POST",
        body: params,
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout"
            : "Payout Management",
        },
      }),
    }),
    getChargebackReport: builder.query<DataResponse<Chargeback[]>, ChargebackRequest>({
      query: (params) => ({
        url: `/report/chargebacks`,
        method: "GET",
        params,
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargeback",
        },
      }),
    }),
    getChargebackReportPost: builder.mutation<DataResponse<Chargeback[]>, ChargebackRequest>({
      query: (params) => ({
        url: `/report/chargebacks/get-new-list`,
        method: "POST",
        body: params,
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargeback",
        },
      }),
    }),
    createChargebackReport: builder.mutation<DataResponse<Chargeback>, Chargeback>({
      query: ({ id, ...others }) => ({
        url: `/report/chargebacks`,
        body: others,
        method: "POST",
        headers: {
          PageName: "Chargeback Management",
        },
      }),
    }),
    updateChargebackReport: builder.mutation<DataResponse<Chargeback>, Chargeback>({
      query: ({ id, ...others }) => ({
        url: `/report/chargebacks/${id}`,
        body: { id, ...others },
        method: "PUT",
        headers: {
          PageName: "Chargeback Management",
        },
      }),
    }),
    getDetailChargebackReport: builder.query<DataResponse<Chargeback>, { id: number }>({
      query: ({ id }) => ({
        url: `/report/chargebacks/${id}`,
        method: "GET",
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargeback",
        },
      }),
    }),
    getDisbursementPayout: builder.query<any, any>({
      query: (params) => ({
        url: `/report/disbursements/${params?.id}/payout`,
        method: "GET",
        params,
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout"
            : "Payout Management",
        },
      }),
    }),
    updateDisbursementPayout: builder.mutation<any, any>({
      query: ({ id, ...others }) => ({
        url: `/report/disbursements/${id}/payout`,
        body: others,
        method: "PUT",
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout"
            : "Payout Management",
        },
      }),
    }),
    getDisbursementPayoutLogs: builder.query<{ id: string }, any>({
      query: (params) => ({
        url: `/report/disbursements/${params?.id}/logs`,
        method: "GET",
        params,
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout-Logs"
            : "Payout Management-Logs",
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          const data = rawData?.data?.messages;
          const newResponseData: any = [];
          data.forEach((item: any) => {
            let data: any = {};
            try {
              data = JSON.parse(item?.message?.message);
            } catch (e) {
              data = {};
            }

            const obj: any = {
              email: item?.message?.email,
              created_at: item?.message?.timestamp,
              affected: data?.affected,
            };
            if (data?.payable_amount !== undefined) {
              obj.payable_amount = data?.payable_amount;
              obj.payable_currency = data?.payable_currency;
            }
            if (data?.status) {
              obj.status = data?.status;
            }
            if (data?.description) {
              obj.description = data?.description;
            }
            if (data?.internal_note) {
              obj.internal_note = data?.internal_note;
            }

            newResponseData.push(obj);
          });
          newResponseData?.sort(
            (a: any, b: any) =>
              new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime()
          );
          return newResponseData;
        }
        return [];
      },
    }),
    getChargebackLogs: builder.query<{ id: string }, any>({
      query: (params) => ({
        url: `/report/chargebacks/${params?.id}/logs`,
        method: "GET",
        params,
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargebacks",
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          const data = rawData?.data?.messages;
          const newResponseData: any = [];
          data.forEach((item: any, index: number) => {
            let logs: any = {};

            try {
              logs = JSON.parse(item?.message?.logs);
            } catch (e) {
              logs = {};
            }

            const obj: any = {
              email: item?.message?.email,
              ref_id: item?.message?.ref_id,
              created_at: item?.message?.timestamp,
              ...logs
            };

            newResponseData.push(obj);
          });
          newResponseData?.sort(
            (a: any, b: any) =>
              new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime()
          );
          return newResponseData;
        }
        return [];
      },
    }),
    getChargeAutomation: builder.query<any, any>({
      query: (params) => ({
        url: `/report/automationCharges`,
        method: "GET",
        params,
        headers: {
          PageName: "Charge Automation",
        },
      }),
    }),
    getChargeAutomationPost: builder.mutation<any, any>({
      query: (params) => ({
        url: `/report/automationCharges`,
        method: "POST",
        body: params,
        headers: {
          PageName: "Charge Automation",
        },
      }),
    }),
    getDetailChargeAutomation: builder.query<any, any>({
      query: (params) => ({
        url: `/report/automationCharges/${params?.id}/logs`,
        method: "GET",
        headers: {
          PageName: "Charge Automation",
        },
        params,
      }),
    }),
    downloadChargeAutomation: builder.query<any, ChargeAutomationDownloadRequest>({
      query: ({ nameMerchant, ...others }) => ({
        url: `/report/automationCharges/download`,
        method: "GET",
        params: others,
        headers: {
          PageName: "Charge Automation",
        },
      }),
    }),
    downloadChargeAutomationPost: builder.mutation<any, ChargeAutomationDownloadRequest>({
      query: ({ nameMerchant, ...others }) => ({
        url: `/report/automationCharges/download`,
        method: "POST",
        body: others,
        headers: {
          PageName: "Charge Automation",
        },
      }),
    }),
    downloadTransactions: builder.query<any, TransactionDownloadRequest>({
      query: ({ nameMerchant, skip_log, ...others }) => ({
        url: `/report/transactions/download`,
        method: "GET",
        params: others,
        headers: !skip_log
          ? {
              PageName: "Transaction",
            }
          : {},
      }),
    }),
    downloadTransactionsPost: builder.mutation<any, any>({
      query: ({ nameMerchant, skip_log, ...others }) => ({
        url: `/report/transactions/download`,
        method: "POST",
        body: others,
        headers: !skip_log
          ? {
              PageName: "Transaction",
            }
          : {},
      }),
    }),
    downloadDisbursements: builder.query<any, DisbursementDownloadRequest>({
      query: ({ nameMerchant, ...others }) => ({
        url: `/report/disbursements/download`,
        method: "GET",
        params: others,
        headers: {
          PageName: "Payout",
        },
      }),
    }),
    downloadDisbursementsPost: builder.mutation<any, DisbursementDownloadRequest>({
      query: ({ nameMerchant, ...others }) => ({
        url: `/report/disbursements/download`,
        method: "POST",
        body: others,
        headers: {
          PageName: "Payout",
        },
      }),
    }),
    downloadChargebacks: builder.query<any, ChargebackDownload>({
      query: ({ nameMerchant, ...others }) => ({
        url: `report/chargebacks/download`,
        method: "GET",
        params: others,
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargeback",
        },
      }),
    }),
    downloadChargebacksPost: builder.mutation<any, ChargebackDownload>({
      query: ({ nameMerchant, ...others }) => ({
        url: `/report/chargebacks/download`,
        method: "POST",
        body: others,
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargeback",
        },
      }),
    }),
    updateMultipleDisbursementPayout: builder.mutation<any, any>({
      query: (params) => ({
        url: `/report/disbursements/complete-multi`,
        body: params,
        method: "POST",
        headers: {
          PageName: "Payout Management",
        },
      }),
    }),
  }),
});

export const {
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
  useGetTransactionsPostMutation,
  useLazyDownloadTransactionsQuery,
  useDownloadTransactionsPostMutation,

  useGetDisbursementReportQuery,
  useLazyGetDisbursementReportQuery,
  useGetDisbursementReportPostMutation,
  useLazyGetDisbursementPayoutQuery,
  useLazyDownloadDisbursementsQuery,
  useDownloadDisbursementsPostMutation,
  useLazyGetDisbursementPayoutLogsQuery,
  useUpdateDisbursementPayoutMutation,

  useGetChargeAutomationQuery,
  useLazyGetChargeAutomationQuery,
  useGetChargeAutomationPostMutation,
  useLazyGetDetailChargeAutomationQuery,
  useLazyDownloadChargeAutomationQuery,
  useDownloadChargeAutomationPostMutation,

  useGetChargebackReportQuery,
  useGetDetailChargebackReportQuery,
  useLazyGetDetailChargebackReportQuery,
  useLazyGetChargebackReportQuery,
  useGetChargebackReportPostMutation,
  useCreateChargebackReportMutation,
  useUpdateChargebackReportMutation,
  useLazyGetChargebackLogsQuery,
  useLazyDownloadChargebacksQuery,
  useDownloadChargebacksPostMutation,
  useUpdateMultipleDisbursementPayoutMutation
} = reportService;
